import type { ReactNode } from 'react'
import { useState } from 'react'
import type { Emoji } from 'emoji-mart'
import { ReactEditor, useSlate } from 'slate-react'
import { twMerge } from 'tailwind-merge'
import EmbeddedVideoAttachment from 'modules/attachments/components/embedded-video-attachment'
import { GifAttachment } from 'modules/attachments/components/gif-attachment'
import { UploadAttachment } from 'modules/attachments/components/upload-attachment'
import { EmojiPicker } from 'modules/comments/components/comment-editor/elements/emoji-picker/emoji-picker'
import { toolbarGroups } from '../../../utils/constants'
import LinkButton from '../elements/Link/link-button'
import { LinkModal } from '../elements/Link/link-modal'
import MentionButton from '../elements/Mention/mention-button'
import { insertText } from '../utils/serialize'

export interface ToolbarProps {
  className?: string
  path?: string
}

export const Toolbar = ({ path, className = '' }: ToolbarProps): ReactNode => {
  const editor = useSlate()
  const [linkIsEditing, setLinkIsEditing] = useState(false)

  const handleMentionClick = (): void => {
    insertText(editor, '@')
    ReactEditor.focus(editor) // Focus the editor after inserting the mention
  }

  const handleAddEmoji = (emoji: typeof Emoji.Props): void => {
    insertText(editor, emoji.native)
    ReactEditor.focus(editor)
  }

  return (
    <div
      className={twMerge('flex items-center gap-2 md:gap-3', className)}
      onClick={e => {
        e.stopPropagation()
      }}
    >
      <UploadAttachment
        className="group"
        iconClassName="size-5 text-text-secondary group-hover:text-text-primary md:size-6"
      />
      <EmbeddedVideoAttachment iconClassName="size-5 md:size-6" />
      <GifAttachment iconClassName="size-5 text-text-secondary hover:text-text-primary md:size-6" />
      {!linkIsEditing && (
        <>
          {toolbarGroups.map(element => {
            switch (element.type) {
              case 'link': {
                return <LinkButton key={element.id} setLinkIsEditing={setLinkIsEditing} />
              }
              case 'mention': {
                return path && <MentionButton key={element.id} onClick={handleMentionClick} />
              }
              default: {
                return <div key={element.id} />
              }
            }
          })}
          <EmojiPicker iconClassName="size-5 md:size-6" onEmojiSelect={handleAddEmoji} />
        </>
      )}
      <LinkModal setLinkIsEditing={setLinkIsEditing} linkIsEditing={linkIsEditing} />
    </div>
  )
}
